import {
  Text,
  styled,
  TextAppearance,
  TextAlignment,
  Container,
  ContainerPadding,
  Breakpoints,
  BreakpointWrapper
} from '@volkswagen-onehub/components-core';
import React, { useEffect, useState } from 'react';
import { StarFilled, StarOutline } from 'src/icons-core-imports';
import { useOneFormContext } from 'src/feature-app/OneForm';
import { useDispatch, useSelector } from 'react-redux';
import { useFormContext } from 'react-hook-form';
import { OneFormState } from 'src/types';
import { InputButton } from 'src/components';

const StarWrapper = styled.div<{ isPrevStep?: boolean; textUnderStars?: boolean }>`

  padding-right: ${(props) => (props.isPrevStep ? '20px' : '32px')};
  padding-right: ${(props) => (props.textUnderStars && '0px')};
  padding-left: ${(props) => (props.textUnderStars ? '32px' : '0px')};
  padding-top: ${(props) => (props.textUnderStars ? '32px' : '0px')};

  cursor: ${(props) => (props.isPrevStep ? null : 'pointer')};
  
  svg {
    width: 32px;
    height: 32px;
    
    @media all and (min-width: 560px) {
      width: ${(props) => (props.isPrevStep ? '20px' : '40px')};
      height: ${(props) => (props.isPrevStep ? '20px' : '40px')};
    }
    @media all and (min-width: 1600px) {
      width: ${(props) => (props.isPrevStep ? '20px' : '48px')};
      height: ${(props) => (props.isPrevStep ? '20px' : '48px')};
    }
  }
  
  @media all and (max-width: 400px) {
    padding-left: ${(props) => (props.textUnderStars ? '7%' : '2vw')};
    padding-right: ${(props) => (props.textUnderStars ? '0px' : '2vw')};

    svg{
      width: ${(props) => (props.isPrevStep ? '10px' : '30px')};
      height: ${(props) => (props.isPrevStep ? '10px' : '30px')};
    }
  }

  &.filled {
    color: #ffd100;
  }

  &.outline {
    color: #6a767d;
  }
`;

const StarText = styled.div`
  display: flex;
  justify-content: center;
  div{
    position: absolute;
  }
`;

const TextMini = styled.div`
  color: rgb(0, 0, 0);
  font-size: 3vw;
  margin-top: 2px;
  @media all and (min-width: 400px){
    font-size: 12px;
    margin-top: 0;
  }
`;

interface RatingStarsProps {
  name?: string;
  nextOnClick?: boolean;
  preSelectedRate?: number;
  stars?: number;
  isRequired?: boolean;
  isPrevStep?: boolean;
  textBad?: string;
  textGood?: string;
  textUnderStars?: boolean;
  starsText?: string[];
  CTASinRespuesta?: boolean;
}

export const RatingStars = (props: RatingStarsProps) => {
  const {
    name,
    nextOnClick,
    stars,
    isRequired,
    isPrevStep,
    preSelectedRate,
    textBad,
    textGood,
    textUnderStars,
    starsText,
    CTASinRespuesta
  } = props;
  const { handleNextStep } = useOneFormContext();
  const { register, setValue } = useFormContext();
  const [rateValue, setRateValue] = useState<number>(preSelectedRate ? preSelectedRate : 0);
  const dispatch = useDispatch();
  const { formData } = useSelector((state: OneFormState) => state);
  const numberOfStars = stars ? stars : 5;

  useEffect(() => {
    if (formData.fields[name]) {
      if (formData.fields[name] === 'Sin respuesta') {
        setTimeout(() => {
          setValue(name, 'Sin respuesta');
        }, 1000);
        setRateValue(0);
      } else {
        setRateValue(Number(formData.fields[name]));
      }
    }
  }, []);

  const handleClick = (i: number, e: any) => {
    const rateValue = i + 1;
    setRateValue(rateValue);
    dispatch({ type: 'UPDATE_FIELDS', payload: { [name]: `${rateValue}` } });

    if (nextOnClick) {
      setTimeout(() => {
        handleNextStep(e);
      }, 0);
    }
  };

  const renderStarsText = (starsText, index: number) => {
    if (starsText[index] === undefined) {
      return <hr />
    }
    else return (
      <StarText>
        <BreakpointWrapper min={Breakpoints.b560}>
          <Text appearance={TextAppearance.copy0100} staticSize>{starsText[index]}</Text>
        </BreakpointWrapper>
        <BreakpointWrapper max={Breakpoints.b560}>
            <TextMini>{starsText[index]}</TextMini>
        </BreakpointWrapper>
      </StarText>
    );
  };

  const renderStars = (rateValue: number, numberOfStars: number) => {
    const auxArr = [];
    for (let i = 0; i < numberOfStars; i++) {
      if (i <= rateValue - 1) {
        auxArr.push(
          <StarWrapper
            className="filled"
            key={i}
            onClick={(e: any) => {
              e.preventDefault();
              if (isPrevStep) {
                return;
              } else {
                handleClick(i, e);
              }
            }}
            isPrevStep={isPrevStep}
            textUnderStars={textUnderStars}
          >
            <StarFilled />
            {textUnderStars &&
              (
                renderStarsText(starsText, i)
              )
            }
          </StarWrapper>
        );
      } else {
        auxArr.push(
          <StarWrapper
            className="outline"
            key={i}
            onClick={(e: any) => {
              e.preventDefault();
              if (isPrevStep) {
                return;
              } else {
                handleClick(i, e);
              }
            }}
            isPrevStep={isPrevStep}
            textUnderStars={textUnderStars}
          >
            <StarOutline variant="large" />
            {textUnderStars &&
              (
                renderStarsText(starsText, i)
              )
            }
          </StarWrapper>
        );
      }
    }

    return auxArr;
  };

  const handleField = () => {
    // Hack para solucionar el problema que se da en el caso de elegir Sin respuesta y volver hacia atrás.
    if (formData.fields[name] === 'Sin respuesta') {
      setTimeout(() => {
        setValue(name, 'Sin respuesta');
      }, 0);
    }
  };

  return (
    <div onMouseMove={handleField} onMouseOut={handleField} onMouseEnter={handleField}>
      {isPrevStep || textUnderStars ? null : (
        <Container padding={{ bottom: ContainerPadding.dynamic0100, top: ContainerPadding.dynamic0050 }}>
          <Text appearance={TextAppearance.copy0200} staticSize>
            Valora del 1 al 5, siendo 1 {textBad} y 5 totalmente{' '}
            <span style={{ whiteSpace: 'nowrap' }}>
              {textGood}
              <sup style={{ top: 'auto', fontSize: '100%' }}>*</sup>.
            </span>
          </Text>
        </Container>
      )}
      <div style={{ display: 'flex' }}>{renderStars(rateValue, numberOfStars)}</div>
      {isPrevStep ? null : (
        <input
          type="hidden"
          name={name}
          value={rateValue === 0 ? null : rateValue}
          ref={register({ required: true })}
        />
      )}
      {CTASinRespuesta ? (
        <div style={{ paddingTop: '24px' }}>
          <InputButton
            selectedValue={formData.fields[name]}
            tag="button"
            name={name}
            label="Sin respuesta"
            key={10}
            onClick={(e) => {
              handleNextStep(e);
            }}
          />
        </div>
      ) : null}
    </div>
  );
};
